import React, { useMemo } from 'react';

/**
 * Deterministic pseudo random generator.
 * Keeps the star field stable between renders instead of reshuffling on every update.
 */
const createRandom = (seed: number) => {
  let value = seed;
  return (): number => {
    value = (value * 1664525 + 1013904223) % 4294967296;
    return value / 4294967296;
  };
};

interface Star {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
  opacity: number;
}

const Hero: React.FC = () => {
  const stars = useMemo<Star[]>(() => {
    const random = createRandom(20260927);

    return Array.from({ length: 170 }, (_, id) => ({
      id,
      x: Number((random() * 100).toFixed(3)),
      y: Number((random() * 74).toFixed(3)),
      size: Number((random() * 1.6 + 0.4).toFixed(2)),
      delay: Number((random() * 7).toFixed(2)),
      duration: Number((random() * 5 + 3).toFixed(2)),
      opacity: Number((random() * 0.55 + 0.25).toFixed(2)),
    }));
  }, []);

  return (
    <section className="hero section" id="home">
      <div className="hero__media" aria-hidden="true">
        <div className="hero__stars">
          {stars.map(star => (
            <span
              key={star.id}
              className="hero__star"
              style={{
                left: `${star.x}%`,
                top: `${star.y}%`,
                width: `${star.size}px`,
                height: `${star.size}px`,
                opacity: star.opacity,
                animationDelay: `${star.delay}s`,
                animationDuration: `${star.duration}s`,
              }}
            />
          ))}
        </div>

        <div className="hero__grid" />
        <div className="hero__grain" />
        <div className="hero__vignette" />
      </div>

      <div className="hero__telemetry" aria-hidden="true">
        <p className="hero__telemetry-row">
          <span className="hero__led" /> Yegor Cherov / Portfolio
        </p>
        <p className="hero__telemetry-row hero__telemetry-row--muted">
          <span className="hero__led hero__led--ice" /> Software and systems
        </p>
        <p className="hero__telemetry-row hero__telemetry-row--muted">
          <span className="hero__led hero__led--signal" /> Hardware and automation
        </p>
      </div>

      <div className="hero__content">
        <p className="hero__eyebrow">Portfolio / 2026</p>

        <h1 className="hero__title">
          <span className="hero__line hero__line--solid">Yegor</span>
          <span className="hero__line hero__line--solid">Cherov</span>
          <span className="hero__line hero__line--outline">Engineer</span>
          <span className="hero__line hero__line--outline">At Work</span>
        </h1>

        <p className="hero__subtitle">Engineer / Software, systems, and hardware</p>

        <p className="hero__text">
          Software, tools, and physical objects.
        </p>

        <div className="hero__btns">
          <a href="#work" className="btn btn--signal">
            View work <i className="ri-arrow-right-line"></i>
          </a>
          <a href="#contact" className="btn btn--ghost">
            Get in touch
          </a>
        </div>
      </div>

      <a href="#capabilities" className="hero__cue" aria-label="Scroll to capabilities">
        <span className="hero__cue-ring" aria-hidden="true" />
        <i className="ri-arrow-down-line" aria-hidden="true"></i>
      </a>

      <p className="hero__scroll-note" aria-hidden="true">Scroll</p>
    </section>
  );
};

export default Hero;